import * as React from "react";
import { useHistory } from "react-router-dom";
import { RotateCcw, Sparkles } from "lucide-react";
import { Button } from "@/components/ui";
import { cn } from "@/lib/utils";
import { AspectRatio } from "@/services/bedrock/dtos/DesignPackage";
import { URLS } from "@/utils/routes";
import { useDynamicPromptsAccess } from "@/utils/useDynamicPromptsAccess";
import { StyleChips } from "../components/StyleChips";
import { composeBrief } from "../composeBrief";
import { DesignStudioScopeContext } from "../DesignStudioScope";
import type { DesignBasis, NewDesignFormValues } from "../NewDesignPage";
import { chipsFor, deriveContextType, pruneChipIds, toggleChip } from "../styleChips";
import { TEMPLATE_PRESETS } from "../templatePresets";

/** What the builder sets: the New design form's structure and style, without notes. */
export type BriefPicks = Pick<NewDesignFormValues, "basis" | "format" | "aspectRatio" | "chipIds">;

export function defaultPicks(canCreateReviewDesigns: boolean): BriefPicks {
  return { basis: canCreateReviewDesigns ? "review" : "other", format: "single", aspectRatio: "auto", chipIds: [] };
}

/**
 * Where What to ask for was opened, and so what its button does: `apply` hands the picks to the New design
 * form on screen, `start` opens New design with them, `none` shows the builder without a button, and `refine`
 * swaps the builder for refine examples (in the editor), which fill the refine box through `onPick` when set.
 */
export type BriefAction =
  | { kind: "apply"; onApply(picks: BriefPicks): void }
  | { kind: "start" }
  | { kind: "none" }
  | { kind: "refine"; onPick?(text: string): void };

export interface BriefBuilderProps {
  picks: BriefPicks;
  onChange(picks: BriefPicks): void;
  action: BriefAction;
  /** Closes the dialog once the picks are used */
  onDone(): void;
}

interface Option<T extends string> {
  value: T;
  label: string;
}

const BASIS_OPTIONS: Option<DesignBasis>[] = [
  { value: "review", label: "A client review" },
  { value: "other", label: "Something else" },
];

const FORMAT_OPTIONS: Option<"single" | "carousel">[] = [
  { value: "single", label: "Single image" },
  { value: "carousel", label: "Carousel" },
];

const SIZE_OPTIONS: Option<AspectRatio | "auto">[] = [
  { value: "auto", label: "Auto" },
  { value: "square_1_1", label: "1:1" },
  { value: "portrait_4_5", label: "4:5" },
  { value: "portrait_9_16", label: "9:16" },
];

function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Option<T>[];
  onChange(value: T): void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</span>
      <div
        role="group"
        aria-label={label}
        data-testid={`builder-${label.toLowerCase().replace(/\s+/g, "-")}`}
        className="inline-flex w-fit rounded-lg bg-slate-100 p-0.5"
      >
        {options.map(option => {
          const selected = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={selected}
              onClick={() => onChange(option.value)}
              className={cn(
                "cursor-pointer rounded-md px-2.5 py-1 text-sm font-medium transition-colors",
                selected ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"
              )}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function samePicks(a: BriefPicks, b: BriefPicks): boolean {
  return (
    a.basis === b.basis &&
    a.format === b.format &&
    a.aspectRatio === b.aspectRatio &&
    a.chipIds.length === b.chipIds.length &&
    a.chipIds.every(id => b.chipIds.includes(id))
  );
}

/** Pick a combo or tap styles, and see the brief Design Studio would get. */
export const BriefBuilder: React.FC<BriefBuilderProps> = ({ picks, onChange, action, onDone }) => {
  const history = useHistory();
  const { canCreateReviewDesigns, canEdit } = useDynamicPromptsAccess();
  const adminScope = React.useContext(DesignStudioScopeContext);
  const { basis, format, aspectRatio, chipIds } = picks;

  const combos = Object.entries(TEMPLATE_PRESETS).filter(
    ([, preset]) => canCreateReviewDesigns || preset.basis !== "review"
  );
  const chips = chipsFor(basis, format);
  const brief = composeBrief({
    basis,
    contextType: deriveContextType(basis, chipIds),
    format,
    aspectRatio,
    chips: chips.filter(chip => chipIds.includes(chip.id)),
    notes: "",
  });

  // Changing what the post is or its format drops styles that no longer fit, as New design does
  const setStructure = (next: Partial<Pick<BriefPicks, "basis" | "format">>) => {
    const merged = { ...picks, ...next };
    onChange({ ...merged, chipIds: pruneChipIds(merged.chipIds, merged.basis, merged.format) });
  };

  const use = () => {
    if (action.kind === "apply") {
      action.onApply(picks);
    } else {
      const state: NewDesignFormValues = { ...picks, notes: "", referenceImageUrl: undefined, templateId: null };
      history.push(adminScope ? URLS.admin.adminNewDesign : URLS.coach.newDesign, state);
    }
    onDone();
  };
  const showAction = action.kind === "apply" || (action.kind === "start" && canEdit);

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Start from a combo</span>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-3" data-testid="builder-combos">
          {combos.map(([name, preset]) => {
            const selected = samePicks(picks, preset);
            return (
              <button
                key={name}
                type="button"
                aria-pressed={selected}
                onClick={() =>
                  onChange({
                    basis: preset.basis,
                    format: preset.format,
                    aspectRatio: preset.aspectRatio,
                    chipIds: preset.chipIds,
                  })
                }
                className={cn(
                  "flex cursor-pointer flex-col items-start rounded-lg border px-3 py-2 text-left transition-colors",
                  selected
                    ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                    : "border-slate-200 bg-white hover:border-slate-300"
                )}
              >
                <span className="text-sm font-semibold text-slate-800">{name}</span>
                <span className="text-xs text-slate-500">{preset.teaser}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-3">
        {canCreateReviewDesigns && (
          <Segmented
            label="Built around"
            value={basis}
            options={BASIS_OPTIONS}
            onChange={next => setStructure({ basis: next })}
          />
        )}
        <Segmented
          label="Format"
          value={format}
          options={FORMAT_OPTIONS}
          onChange={next => setStructure({ format: next })}
        />
        <Segmented
          label="Size"
          value={aspectRatio}
          options={SIZE_OPTIONS}
          onChange={next => onChange({ ...picks, aspectRatio: next })}
        />
      </div>

      <div data-testid="builder-chips">
        <StyleChips
          chips={chips}
          selectedIds={chipIds}
          onToggle={chip => onChange({ ...picks, chipIds: toggleChip(chipIds, chip) })}
        />
      </div>

      <div className="space-y-1.5 rounded-xl border border-primary/15 bg-primary/[0.03] p-4">
        <span className="text-xs font-bold uppercase tracking-wider text-primary">What Design Studio hears</span>
        <p className="text-sm leading-relaxed text-slate-700" aria-live="polite" data-testid="builder-brief">
          {brief}
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2" data-testid="builder-actions">
        <Button
          variant="ghost"
          size="sm"
          disabled={samePicks(picks, defaultPicks(canCreateReviewDesigns))}
          onClick={() => onChange(defaultPicks(canCreateReviewDesigns))}
        >
          <RotateCcw aria-hidden="true" />
          Start over
        </Button>
        {showAction && (
          <Button size="sm" onClick={use}>
            <Sparkles aria-hidden="true" />
            {action.kind === "apply" ? "Use these picks" : "Start a design with these"}
          </Button>
        )}
      </div>
    </div>
  );
};
